import { PrescriptionInfoMapper } from "@/data/mapper/prescription/prescription-info-mapper";
import { safeMapToEntity } from "@/data/repo-Implement/safe-map-to-entity";
import { PrescriptionSource } from "@/data/source/prescription-source";
import {
  ActivityDetail,
  EyeActivityDataModel,
  PrescriptionDetail,
  PrescriptionSummary,
} from "@/domain/entity/prescription/prescription-info-entity";
import { PrescriptionRepository } from "@/domain/repository/prescription-repository";
import { SATError, SATResult } from "@sadtx/sat-error";

export class PrescriptionRepositoryImpl implements PrescriptionRepository {
  constructor(private readonly prescriptionSource: PrescriptionSource) {}

  async fetchPrescriptionList(patientUuid: string): Promise<SATResult<PrescriptionSummary[], SATError>> {
    const result = await this.prescriptionSource.fetchPrescriptionList(patientUuid);
    if (result.isFailure) {
      return SATResult.failure(result.error);
    }
    return safeMapToEntity(
      result.value,
      PrescriptionInfoMapper.toSummaryEntityList,
      "PrescriptionRepositoryImpl::fetchPrescriptionList",
    );
  }

  async fetchPrescriptionDetail(
    patientUuid: string,
    prescriptionUuid: string,
  ): Promise<SATResult<PrescriptionDetail, SATError>> {
    const result = await this.prescriptionSource.fetchPrescriptionDetail(patientUuid, prescriptionUuid);
    if (result.isFailure) {
      return SATResult.failure(result.error);
    }

    return safeMapToEntity(
      result.value,
      (dto) => PrescriptionInfoMapper.toDetailEntity(dto, patientUuid, prescriptionUuid),
      "PrescriptionRepositoryImpl::fetchPrescriptionDetail",
    );
  }

  async createPrescription(payload: Record<string, unknown>): Promise<SATResult<void, SATError>> {
    const result = await this.prescriptionSource.createPrescription(payload);
    if (result.isFailure) {
      return SATResult.failure(result.error);
    }
    return SATResult.success(void 0);
  }

  async cancelPrescription(
    patientUuid: string,
    prescriptionUuid: string,
  ): Promise<SATResult<void, SATError>> {
    const result = await this.prescriptionSource.cancelPrescription(patientUuid, prescriptionUuid);
    if (result.isFailure) {
      return SATResult.failure(result.error);
    }
    return SATResult.success(void 0);
  }

  async sendPatientMessage(
    patientUuid: string,
    title: string,
    content: string,
  ): Promise<SATResult<void, SATError>> {
    const result = await this.prescriptionSource.sendPatientMessage(patientUuid, title, content);
    if (result.isFailure) {
      return SATResult.failure(result.error);
    }
    return SATResult.success(void 0);
  }

  async fetchActivityDetail(activityUuid: string): Promise<SATResult<ActivityDetail, SATError>> {
    const result = await this.prescriptionSource.fetchActivityDetail(activityUuid);
    if (result.isFailure) {
      return SATResult.failure(result.error);
    }
    return safeMapToEntity(
      result.value,
      PrescriptionInfoMapper.toActivityDetailEntity,
      "PrescriptionRepositoryImpl::fetchActivityDetail",
    );
  }

  async fetchEyeActivityData(activityUuid: string): Promise<SATResult<EyeActivityDataModel, SATError>> {
    const result = await this.prescriptionSource.fetchEyeActivityData(activityUuid);
    if (result.isFailure) {
      return SATResult.failure(result.error);
    }
    return safeMapToEntity(
      result.value,
      PrescriptionInfoMapper.toEyeActivityDataModelEntity,
      "PrescriptionRepositoryImpl::fetchEyeActivityData",
    );
  }

  async submitActivityFeedback(
    activityUuid: string,
    comment: string,
  ): Promise<SATResult<void, SATError>> {
    const result = await this.prescriptionSource.submitActivityFeedback(
      activityUuid,
      comment,
    );
    if (result.isFailure) {
      return SATResult.failure(result.error);
    }
    return SATResult.success(void 0);
  }
}
